import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import Form, { FORM_HORIZONTAL_LAYOUT } from "../../../components/Form";
import useFormSubmit from "../../../components/Form/useFormSubmit";
import { reloadTable } from "../../../components/SmartTable/reducer";
import { closeModalAction } from "../../../components/Modal/reducer";
import Button from "../../../components/Button";
import { renderDynamicallyFields } from "../../../components/Form/methods";
import { useModalData } from "../../../components/Modal/useModalData";
import { formTransformer } from "../../Core/Users/UserView/UserViewTrgQualifications/methods";
import { useDateFormat } from "../../../utils/hooks/useDateFormat";
import { TABLE_QUALIFICATIONS } from "./methods";
import { buildQualificationFormFields } from "./qualificationFormFields";
import { MODAL_QUALIFICATION } from "./index";

/**
 * In "create" mode (no existing qualification id) it collects the full set of fields including
 * completed date, validity start date, instructor type/details, evidence and certificate uploads.
 * In "edit" mode most fields are read-only and only expiry notification and internal remarks can
 * be changed, because altering historical qualification data would compromise training records.
 *
 * On successful submission the qualifications SmartTable is refreshed and the modal is closed.
 *
 * @returns {React.ReactElement} The qualification create/edit form rendered inside an Ant Design Form.
 */
const QualificationForm = () => {
  const { format } = useDateFormat();
  const data = useModalData(MODAL_QUALIFICATION);
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isNewRecord = !data?.qualification?.id;
  const [training, setTraining] = useState(data.qualification?.training);
  const { loading, submit, generalError } = useFormSubmit({
    form,
    route: isNewRecord ? "trg/qualifications" : `trg/qualifications/${data?.qualification?.id}`,
    method: isNewRecord ? "post" : "put",
    transformer: (values) => ({
      ...(isNewRecord ? { ...formTransformer(format, values) } : values),
      expiry_notification: values.expiry_notification === undefined ? false : values.expiry_notification
    }),
    onSuccess: () => {
      dispatch(reloadTable({ name: TABLE_QUALIFICATIONS }));
      localStorage.setItem("qualificationsPageStateApply", true);
      dispatch(closeModalAction({ name: MODAL_QUALIFICATION }));
    }
  });

  return (
    <Form
      onSubmit={submit}
      form={form}
      onValuesChange={(changedValues, allValues) => {
        if (changedValues.completed_date && !allValues.validity_start_date) {
          form.setFieldsValue({ validity_start_date: changedValues.completed_date });
        }
      }}
      errors={generalError}
      loading={loading}
      layout={FORM_HORIZONTAL_LAYOUT}
      initialValues={{
        ...data,
        user_id: data.user_detail?.user_id ? data.user_detail.user_id : undefined,
        training_id: data.qualification?.training?.id || undefined,
        description: data.qualification?.description || undefined,
        internal_remarks: data.qualification?.internal_remarks || undefined,
        expiry_notification: isNewRecord ? true : data.qualification?.expiry_notification
      }}
      header={
        <>
          <div className="h3">
            {t(isNewRecord ? "trg.add_qualification_modal.form_title" : "trg.edit_qualification_modal.form_title")}
          </div>
          <div className="mb-30 mt-10 h6 font-weight-300 text-muted">{t("container.general_words.form_subtitle")}</div>
        </>
      }
    >
      {renderDynamicallyFields(buildQualificationFormFields({ data, training, isNewRecord, form, t, setTraining }))}
      <div className="flex mt-25 flex-justify-end">
        <Button
          className="min-w-150"
          type="primary"
          htmlType="submit"
          disabled={loading}
          loading={loading}
          icon={<i className="ti-check" />}
        >
          {t("container.general_words.save")}
        </Button>
      </div>
    </Form>
  );
};

export default QualificationForm;
